// OAuth consent page — /connect/authorize?req=<id>
//
// ChatGPT (or any MCP client) is redirected here by the backend's OAuth
// server (backend/api/oauthServer.js). The user signs in with their normal
// fast.legal account, sees which app is asking and for what, and approves
// or denies. Approval POSTs the Firebase ID token to /oauth/consent; the
// backend mints the authorization code and we send the browser back to the
// client's redirect URI. No shell (navbar/sidebar) — it's a focused dialog.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import Login from '../components/Login';
import Icon from '../components/Icons';
import './ConnectAuthorize.css';

const API = process.env.REACT_APP_UPLOAD_API || '';

const SCOPE_COPY = {
  read: { label: 'Read your projects', hint: 'Projects, documents, versions, deal points, knowledge, team, usage — everything you can see in the app.' },
  write: { label: 'Act in your projects', hint: 'Create and edit documents, publish versions, record deal points, run the drafting agent, send for signature — the same actions you can take in the app, under your permissions.' },
  admin: { label: 'Manage organizations', hint: 'Invite members, change roles, manage billing — only where you are already an org admin.' },
  openid: { label: 'Know who you are', hint: 'Your user id, so the app can tell your sessions apart.' },
  email: { label: 'See your email address', hint: 'Shown inside the connected app as your identity.' },
};

export default function ConnectAuthorize() {
  const [params] = useSearchParams();
  const reqId = params.get('req') || '';
  const { user, loading } = useAuth();
  const [req, setReq] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!reqId) { setError('Missing request id — start again from the connecting app.'); return; }
    if (!API) { setError('API not configured.'); return; }
    fetch(`${API}/oauth/request/${encodeURIComponent(reqId)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || `request lookup failed (${r.status})`);
        setReq(data);
        setChosen(data.scopes);
      })
      .catch((e) => setError(e.message));
  }, [reqId]);

  const decide = async (approve) => {
    setBusy(true);
    setError(null);
    try {
      const token = await firebase.auth().currentUser.getIdToken();
      const r = await fetch(`${API}/oauth/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requestId: reqId, approve, scopes: chosen }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `consent failed (${r.status})`);
      window.location.replace(data.redirect);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const signedIn = user && !user.isAnonymous;
  const toggle = (s) => setChosen((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <main className="cx-wrap" id="main">
      <div className="cx-card">
        <div className="cx-brand">
          <span className="cx-logo">fast.legal</span>
          <span className="cx-link"><Icon name="key" size={14} /></span>
          <span className="cx-app">
            {req?.logoUri ? <img src={req.logoUri} alt="" className="cx-app-logo" /> : null}
            {req?.clientName || 'Connecting app'}
          </span>
        </div>

        {error && <div className="cx-error" role="alert">{error}</div>}

        {!error && !req && <div className="cx-muted">Loading request…</div>}

        {req && !signedIn && !loading && (
          <>
            <h1 className="cx-title">Sign in to continue</h1>
            <p className="cx-muted">
              <strong>{req.clientName}</strong> wants to connect to your fast.legal account. Sign in first;
              you'll review what it can do on the next step.
            </p>
            <div className="cx-login"><Login /></div>
          </>
        )}

        {req && signedIn && (
          <>
            <h1 className="cx-title">Allow {req.clientName} to use fast.legal?</h1>
            <p className="cx-muted">
              Signed in as <strong>{user.email}</strong>. The app will act as you, inside the organizations and
              projects you already have access to. You can disconnect it any time from Team → Connected apps.
            </p>
            <ul className="cx-scopes">
              {req.scopes.map((s) => (
                <li key={s}>
                  <label className="cx-scope">
                    <input
                      type="checkbox"
                      checked={chosen?.includes(s) || false}
                      onChange={() => toggle(s)}
                      disabled={busy || s === 'openid'}
                    />
                    <span>
                      <span className="cx-scope-label">{SCOPE_COPY[s]?.label || s}</span>
                      <span className="cx-scope-hint">{SCOPE_COPY[s]?.hint || ''}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="cx-fine">
              After approving you'll be sent to <code>{req.redirectHost}</code>. Every action the app takes is
              recorded in your project audit trails as “you via ChatGPT”.
            </p>
            <div className="cx-actions">
              <button type="button" className="cs-btn cs-btn-ghost" disabled={busy} onClick={() => decide(false)}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-primary" disabled={busy || !chosen?.length} onClick={() => decide(true)}>
                {busy ? 'Connecting…' : 'Allow'}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
